import { useBrowsePresetApi } from '@aibees/shared/api';
import {
  CreatePresetRequest,
  HttpMethod,
  Maybe,
  ParsedPreset,
} from '@aibees/shared/definition';
import { useDebounceFn } from '@aibees/shared/hooks';
import {
  baseQueryClient,
  baseQueryClientUpdatePaginatedQueries,
} from '@aibees/shared/http/base-query-client';
import { useBaseMutation } from '@aibees/shared/http/hooks';
import { jsonParse, jsonStringify } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { PRESET_API_PREFIX } from './constants';

export function useCreatePresetMutationApi<T = any>(
  allowDuplication?: boolean,
  shareable?: boolean,
  doNotShowErrorNotification?: boolean,
  returnDuplicatedValue?: boolean
) {
  const workspace = useWorkspace()?.id;

  const invalidateBrowsePresets = useDebounceFn(
    (data: Maybe<ParsedPreset<T>>) =>
      baseQueryClient.invalidateQueries([useBrowsePresetApi.name, data?.type]),
    1000
  );

  return useBaseMutation<
    CreatePresetRequest<T>,
    ParsedPreset<T>,
    CreatePresetRequest<T>
  >(
    {
      url: PRESET_API_PREFIX.replace(':id', String(workspace)),
      method: HttpMethod.post,
    },
    {
      onMutate(variables: CreatePresetRequest<T>) {
        variables.allowDuplication = allowDuplication;
        variables.shareable = shareable;
        variables.returnDuplicatedValue = returnDuplicatedValue;
        variables.value =
          (jsonStringify(variables.value) as T) ??
          (jsonStringify({}) as string as T);
        variables.metadata = variables.metadata
          ? (jsonStringify(variables.metadata) as string)
          : undefined;
      },
      onSettled(data) {
        invalidateBrowsePresets(data);
      },
      onSuccess: (response) => {
        response.value = jsonParse(response.value, response.value);
        baseQueryClientUpdatePaginatedQueries(
          [useBrowsePresetApi.name, response.type],
          response
        );
      },
      doNotShowErrorNotification: !!doNotShowErrorNotification,
    }
  );
}
